import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminUser } from '../lib/authService'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Check,
  X,
  Zap,
  TrendingUp,
  Crown,
  Heart,
  Sparkles,
  Star,
  Shield,
  UserPlus,
  BookOpen,
} from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'

interface PricingProps {
  user: AdminUser | null
}

interface PlanFeature {
  name: string
  foundation: boolean | string | number
  starter: boolean | string | number
  growth: boolean | string | number
  leadership: boolean | string | number
}

export default function Pricing({ user }: PricingProps) {
  const navigate = useNavigate()
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')
  const { limits } = useSubscription(user?.churchId)

  const plans = [
    {
      name: 'Foundation',
      value: 'trial',
      tagline: 'Get started for free',
      price: {
        monthly: 0,
        annual: 0,
      },
      icon: <Heart className="h-6 w-6" />,
      color: 'from-gray-500 to-gray-600',
      description: 'Basic tools for small churches just getting started',
      features: [
        '100 members',
        '2 admin accounts',
        '1GB storage',
        'Member management',
        'Groups & ministries',
        'Basic attendance',
        'Events calendar',
        'Prayer requests',
      ],
      highlighted: false,
    },
    {
      name: 'Starter',
      value: 'starter',
      tagline: 'Perfect for small to medium churches',
      price: {
        monthly: 199,
        annual: 1910,
      },
      icon: <Zap className="h-6 w-6" />,
      color: 'from-blue-500 to-cyan-500',
      description: 'Essential tools to manage your growing congregation',
      features: [
        '500 members',
        '5 admin accounts',
        '10GB storage',
        '300 SMS per month',
        'Member management',
        'Groups & ministries',
        'Basic attendance',
        'Events calendar',
        'Prayer requests',
        'Basic finance tracking',
      ],
      highlighted: false,
    },
    {
      name: 'Growth',
      value: 'growth',
      tagline: 'Scale your ministry with powerful features',
      price: {
        monthly: 499,
        annual: 4790,
      },
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'from-sky-500 to-cyan-400',
      description: 'Everything you need to manage a growing church',
      features: [
        '2,000 members',
        'Unlimited admin accounts',
        '50GB storage',
        '1,000 SMS per month',
        'Everything in Starter, plus:',
        'Complete finance (GL, budgets)',
        'QR code attendance',
        'Ministry scheduling',
        'Counseling management',
        'Media library',
        'Evangelism tracking',
        'Advanced reporting',
      ],
      highlighted: true,
    },
    {
      name: 'Leadership',
      value: 'leadership',
      tagline: 'Enterprise solutions for large churches',
      price: {
        monthly: 1499,
        annual: 14390,
      },
      icon: <Crown className="h-6 w-6" />,
      color: 'from-amber-500 to-orange-500',
      description: 'Complete church management for large congregations',
      features: [
        'Unlimited members',
        'Unlimited admin accounts',
        '500GB storage',
        '3,000 SMS per month',
        'Everything in Growth, plus:',
        'HR & payroll system',
        'White-label mobile app',
        'API access',
        'Multi-campus support',
        'Priority support (4-hour)',
        'Custom integrations',
        'Dedicated account manager',
      ],
      highlighted: false,
    },
  ]

  const featureComparison: PlanFeature[] = [
    {
      name: 'Member Management',
      foundation: '100 members',
      starter: '500 members',
      growth: '2,000 members',
      leadership: 'Unlimited',
    },
    {
      name: 'Admin Accounts',
      foundation: '2 admins',
      starter: '5 admins',
      growth: 'Unlimited',
      leadership: 'Unlimited',
    },
    {
      name: 'Storage Space',
      foundation: '1GB',
      starter: '10GB',
      growth: '50GB',
      leadership: '500GB',
    },
    {
      name: 'SMS per Month',
      foundation: false,
      starter: '300 SMS',
      growth: '1,000 SMS',
      leadership: '3,000 SMS',
    },
    {
      name: 'Storage Add-ons',
      foundation: false,
      starter: 'GHS 50/10GB',
      growth: 'GHS 50/10GB',
      leadership: 'GHS 50/10GB',
    },
    {
      name: 'Communications & Events',
      foundation: true,
      starter: true,
      growth: true,
      leadership: true,
    },
    {
      name: 'Prayer Requests',
      foundation: true,
      starter: true,
      growth: true,
      leadership: true,
    },
    {
      name: 'Mobile App Access',
      foundation: true,
      starter: true,
      growth: true,
      leadership: true,
    },
    {
      name: 'Basic Finance Tracking',
      foundation: false,
      starter: true,
      growth: true,
      leadership: true,
    },
    {
      name: 'Complete Finance (GL, Budgets)',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'QR Code Attendance',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'Ministry Scheduling',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'Counseling Management',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'Media Library',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'Evangelism Tracking',
      foundation: false,
      starter: false,
      growth: true,
      leadership: true,
    },
    {
      name: 'Multi-Campus Support',
      foundation: false,
      starter: '1 location',
      growth: '1 location',
      leadership: 'Unlimited',
    },
    {
      name: 'HR & Payroll',
      foundation: false,
      starter: false,
      growth: false,
      leadership: true,
    },
    {
      name: 'White-label Mobile App',
      foundation: false,
      starter: false,
      growth: false,
      leadership: true,
    },
    {
      name: 'API Access',
      foundation: false,
      starter: false,
      growth: false,
      leadership: true,
    },
    {
      name: 'Support Level',
      foundation: 'Community',
      starter: 'Email (48h)',
      growth: 'Priority (24h)',
      leadership: 'Phone (4h)',
    },
  ]

  // Features unique to FaithYoke
  const uniqueFeatures = [
    {
      title: 'Evangelism CRM',
      description: 'Track outreach contacts from first encounter to discipleship. No other church platform has this.',
      icon: <UserPlus className="h-6 w-6" />,
      plans: ['Growth', 'Leadership'],
    },
    {
      title: 'Prayer Wall + Fasting Tracker',
      description: 'Dedicated prayer request wall, fasting campaigns, and testimony sharing in one integrated system.',
      icon: <Heart className="h-6 w-6" />,
      plans: ['All Plans'],
    },
    {
      title: 'Bible + Devotions + Reading Plans',
      description: 'Built-in Bible reader with devotionals and church-wide reading plans. No competitor bundles this.',
      icon: <BookOpen className="h-6 w-6" />,
      plans: ['All Plans'],
    },
    {
      title: 'Welfare Check System',
      description: 'Identify absent members automatically and assign follow-up visits. Unique to FaithYoke.',
      icon: <Shield className="h-6 w-6" />,
      plans: ['Growth', 'Leadership'],
    },
    {
      title: 'Full Accounting + HR/Payroll',
      description: 'Complete double-entry accounting with GL, budgets, and full HR/payroll at a fraction of competitor prices.',
      icon: <Sparkles className="h-6 w-6" />,
      plans: ['Leadership'],
    },
  ]

  const faqs = [
    {
      question: 'Can I switch plans at any time?',
      answer:
        'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, changes take effect at the end of your current billing period.',
    },
    {
      question: 'What happens if I exceed my member limit?',
      answer:
        'You\'ll receive a notification when you\'re close to your member limit. To add more members, simply upgrade to the next plan. Your data is always safe and accessible.',
    },
    {
      question: 'Can I buy extra storage without upgrading?',
      answer:
        'Yes! All paid plans can purchase storage add-ons at GHS 50 per 10GB per month. This lets you grow your media library without changing your plan.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept mobile money (MTN, Vodafone, AirtelTigo), credit/debit cards (Visa, Mastercard), and bank transfers. All payments are securely processed via PayStack.',
    },
    {
      question: 'Is there a free trial?',
      answer:
        'All new churches start with a 30-day trial of the Growth plan, giving you full access to premium features. After the trial, you can choose any plan including the free Foundation tier.',
    },
    {
      question: 'What currency are the prices in?',
      answer:
        'All prices are in Ghana Cedis (GHS). We are a Ghana-based platform built specifically for African churches.',
    },
  ]

  const handleSelectPlan = (planValue: string) => {
    if (!user) {
      navigate('/login')
      return
    }

    // Foundation is the free tier
    if (planValue === 'trial') {
      navigate('/dashboard')
      return
    }

    // If user is on this plan already
    if (limits?.plan === planValue) {
      navigate('/dashboard')
      return
    }

    // Navigate to checkout
    navigate(`/checkout?plan=${planValue}&billing=${billingCycle}`)
  }

  const renderFeatureValue = (value: boolean | string | number) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="h-5 w-5 text-green-600 mx-auto" />
      ) : (
        <X className="h-5 w-5 text-gray-300 mx-auto" />
      )
    }
    return <span className="text-sm font-medium">{value}</span>
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Choose the Right Plan for Your Church
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-4">
            Affordable pricing that grows with your ministry. Start with a 30-day free trial.
          </p>
          <p className="text-base text-blue-200 max-w-2xl mx-auto mb-8">
            All new churches get 30 days free to try Growth plan features. No credit card required.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className={billingCycle === 'monthly' ? 'font-semibold' : 'text-blue-200'}>
              Monthly
            </span>
            <button
              onClick={() =>
                setBillingCycle(billingCycle === 'annual' ? 'monthly' : 'annual')
              }
              className="relative inline-flex h-7 w-12 items-center rounded-full bg-white/20 transition-colors"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={billingCycle === 'annual' ? 'font-semibold' : 'text-blue-200'}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <Badge className="bg-green-500 hover:bg-green-600">
                Save 20%
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <Card
              key={plan.value}
              className={`relative overflow-hidden transition-all ${
                plan.highlighted
                  ? 'border-2 border-sky-500 shadow-2xl scale-105 z-10'
                  : 'hover:shadow-xl'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-sky-500 to-cyan-400 text-white px-4 py-1 text-sm font-semibold">
                  Most Popular
                </div>
              )}

              <CardHeader>
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-r ${plan.color} flex items-center justify-center text-white mb-4`}
                >
                  {plan.icon}
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-base">{plan.tagline}</CardDescription>
              </CardHeader>

              <CardContent>
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">
                      {plan.price[billingCycle] === 0
                        ? 'Free'
                        : `GHS ${plan.price[billingCycle].toLocaleString()}`}
                    </span>
                    {plan.price[billingCycle] > 0 && (
                      <span className="text-gray-500 ml-2">
                        /{billingCycle === 'annual' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>
                  {billingCycle === 'annual' && plan.price.annual > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      GHS {Math.round(plan.price.annual / 12).toLocaleString()}/month billed annually
                    </p>
                  )}
                  {billingCycle === 'monthly' && plan.price.monthly > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Save GHS {((plan.price.monthly * 12) - plan.price.annual).toLocaleString()}/year with annual billing
                    </p>
                  )}
                  {plan.price.monthly > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Need more storage? GHS 50/10GB add-on available
                    </p>
                  )}
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">{plan.description}</p>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      {feature.includes('Everything in') ? (
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {feature}
                        </span>
                      ) : (
                        <>
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={() => handleSelectPlan(plan.value)}
                  className={`w-full ${
                    limits?.plan === plan.value
                      ? 'bg-gray-400'
                      : plan.highlighted
                      ? 'bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600'
                      : ''
                  }`}
                  variant={plan.highlighted ? 'default' : 'outline'}
                >
                  {limits?.plan === plan.value
                    ? 'Current Plan'
                    : plan.price.monthly === 0
                    ? 'Get Started Free'
                    : `Get Started with ${plan.name}`}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      {/* What Makes FaithYoke Different */}
      <div className="bg-gradient-to-b from-indigo-50 to-white dark:from-indigo-950/30 dark:to-gray-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200 mb-4">
              <Star className="h-3 w-3 mr-1" />
              Only on FaithYoke
            </Badge>
            <h2 className="text-3xl font-bold">
              What Makes FaithYoke Different
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-2xl mx-auto">
              Features no other church management platform offers, built specifically for African churches
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {uniqueFeatures.map((feature, index) => (
              <Card key={index} className="border-indigo-100 dark:border-indigo-900/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      {feature.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                      <div className="flex gap-1 mt-1">
                        {feature.plans.map((plan) => (
                          <Badge key={plan} variant="secondary" className="text-xs">
                            {plan}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Detailed Feature Comparison
          </h2>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Feature
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Foundation
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Starter
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200 bg-sky-50 dark:bg-sky-900/30">
                      Growth
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Leadership
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {featureComparison.map((feature, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {feature.name}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {renderFeatureValue(feature.foundation)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {renderFeatureValue(feature.starter)}
                      </td>
                      <td className="px-4 py-4 text-center bg-sky-50/50 dark:bg-sky-900/20">
                        {renderFeatureValue(feature.growth)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {renderFeatureValue(feature.leadership)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-400">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Church Management?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join churches already using FaithYoke across Ghana
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100"
              onClick={() => navigate('/signup')}
            >
              Start Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/contact')}
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
